import { useMemo } from "react";
import usePermissions from "./usePermissions";
import { useDiaryContext } from "./useDiaryContext";

export interface BookingPermissions {
  /** Can update/manage a booking (role OR legacy capability) */
  userCanManageBooking: boolean;
  /** Can confirm a booking (role OR legacy capability) */
  userCanConfirmBooking: boolean;
  /** Can save a booking (confirm OR manage) */
  userCanSaveBooking: boolean;
  /** Can create a new booking */
  userCanCreateBooking: boolean;
  /** Can create deposits on a booking */
  userCanCreateDeposits: boolean;
  /** Can manage (update) deposits on a booking */
  userCanManageDeposits: boolean;
  /** Can manage refunds on a booking */
  userCanManageRefunds: boolean;
  /** Can send messages (SMS/email) on a booking */
  userCanCreateMessages: boolean;
  /** Can download bookings (run sheet, guest list) */
  userCanDownloadBookings: boolean;
  /** Can download customer data */
  userCanDownloadCustomers: boolean;
  /** Can read pre-orders on a booking */
  userCanReadPreOrders: boolean;
  /** Can update pre-orders on a booking */
  userCanUpdatePreOrders: boolean;
  /** Venue config restricted_user_max_confirm value, if set */
  restrictedUserMaxConfirm: number | undefined;
}

/**
 * Derives combined booking permission flags from roles and capabilities.
 * This avoids repeating the same role+capability logic across components.
 *
 * Reference: collins-ui bookingDetails.js lines 483-584 and addBooking.js lines 69-88
 */
export const useBookingPermissions = (): BookingPermissions => {
  const { roles, capabilities } = usePermissions();
  const { venueConfig } = useDiaryContext();

  return useMemo(() => {
    const userCanManageBooking =
      roles.booking.update ||
      capabilities.manageVenueGroupBookings.thisVenueGroup ||
      capabilities.manageVenueBookings.thisVenue;

    const userCanConfirmBooking =
      roles.booking.update ||
      capabilities.confirmVenueGroupBookings.thisVenueGroup ||
      capabilities.confirmVenueBookings.thisVenue;

    const userCanSaveBooking = userCanConfirmBooking || userCanManageBooking;

    const userCanCreateBooking =
      roles.booking.create ||
      capabilities.manageVenueGroupBookings.thisVenueGroup ||
      capabilities.manageVenueBookings.thisVenue ||
      capabilities.confirmVenueGroupBookings.thisVenueGroup ||
      capabilities.confirmVenueBookings.thisVenue;

    const userCanCreateDeposits =
      roles.booking.deposits.create || userCanManageBooking;

    const userCanManageDeposits =
      roles.booking.deposits.update ||
      capabilities.manageVenueGroupRefunds.thisVenueGroup ||
      capabilities.manageVenueRefunds.thisVenue;

    const userCanManageRefunds =
      roles.booking.refunds.create ||
      capabilities.manageVenueGroupRefunds.thisVenueGroup ||
      capabilities.manageVenueRefunds.thisVenue;

    const userCanCreateMessages =
      roles.booking.messages.create ||
      capabilities.manageVenueGroupBookings.thisVenueGroup;

    const userCanDownloadBookings = roles.booking.download;

    const userCanDownloadCustomers = roles.customer.download;

    const userCanReadPreOrders = roles.booking.preorders.read;

    const userCanUpdatePreOrders = roles.booking.preorders.update;

    const restrictedUserMaxConfirm = venueConfig?.restricted_user_max_confirm;

    return {
      userCanManageBooking,
      userCanConfirmBooking,
      userCanSaveBooking,
      userCanCreateBooking,
      userCanCreateDeposits,
      userCanManageDeposits,
      userCanManageRefunds,
      userCanCreateMessages,
      userCanDownloadBookings,
      userCanDownloadCustomers,
      userCanReadPreOrders,
      userCanUpdatePreOrders,
      restrictedUserMaxConfirm
    };
  }, [roles, capabilities, venueConfig?.restricted_user_max_confirm]);
};

export default useBookingPermissions;
